"use client";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { refreshExportJobs } from "@/features/exports/tracker";
import {
  getGetInboxChatQueryKey,
  getGetInboxSummaryQueryKey,
  getGetInboxThreadQueryKey,
  getGetTaskCountsQueryKey,
  getListInboxChatsQueryKey,
  getListInboxThreadsQueryKey,
  getListTasksQueryKey,
  pollNotifications,
} from "@/lib/api/generated";

import { showNotificationToast } from "../components/toast";
import { PERSONAL_TOAST_CATEGORIES } from "../lib/categories";
import { NOTIFICATIONS_KEY } from "./use-notifications";
import { UNREAD_COUNT_KEY } from "./use-unread-count";
import type { Notification } from "../lib/types";

/**
 * Ephemeral pseudo-events that ride the same user channel as real
 * notifications but never persist as bell rows (mirrors the backend's
 * ``credit_changed`` / ``sequence_event`` publishes). Credit events carry
 * the new balance so the client can patch the cache without refetching
 * `/api/billing/credits`.
 */
type CreditEvent = { _type: "credit_changed"; balance?: number };
type SequenceEvent = {
  _type: "sequence_event";
  sequence_id?: string | null;
  job_id?: string | null;
  scope?: string | null;
};
type PollEvent = Notification | CreditEvent | SequenceEvent;

interface PollResult {
  events: PollEvent[];
  server_time: string;
}

type BroadcastMsg =
  | { type: "leader-alive"; rank: number }
  | { type: "events"; events: PollEvent[] };

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function isCreditEvent(e: PollEvent): e is CreditEvent {
  return "_type" in e && e._type === "credit_changed";
}

function isSequenceEvent(e: PollEvent): e is SequenceEvent {
  return "_type" in e && e._type === "sequence_event";
}

function isNotification(e: PollEvent): e is Notification {
  return !("_type" in e);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

/**
 * Refresh inbox queries when a candidate-reply notification lands so an
 * open inbox updates live. Acts on `sequence_replies` notifications that
 * carry `metadata_json.payload.sequence_id` (our inbound-reply shape).
 *
 * NOTE: the field is `metadata_json` (the serialized Notification shape),
 * NOT `metadata` — reading `metadata` silently no-ops and the inbox
 * never updates in real time.
 */
function invalidateInboxForReply(
  queryClient: ReturnType<typeof useQueryClient>,
  ev: Notification,
): void {
  if (ev.category !== "sequence_replies") return;
  const meta = asRecord(ev.metadata_json);
  const payload = asRecord(meta?.payload);
  const sequenceId = asString(payload?.sequence_id);
  if (!sequenceId) return;

  queryClient.invalidateQueries({
    queryKey: getListInboxThreadsQueryKey(sequenceId),
  });
  queryClient.invalidateQueries({
    queryKey: getListInboxChatsQueryKey(sequenceId),
  });
  queryClient.invalidateQueries({
    queryKey: getGetInboxSummaryQueryKey(sequenceId),
  });
  const threadId = asString(payload?.thread_id);
  if (threadId) {
    queryClient.invalidateQueries({
      queryKey: getGetInboxThreadQueryKey(sequenceId, threadId),
    });
  }
  const chatId = asString(payload?.chat_id);
  if (chatId) {
    queryClient.invalidateQueries({
      queryKey: getGetInboxChatQueryKey(sequenceId, chatId),
    });
  }

  // A new reply also changes enrollment engagement/interest analytics and
  // may create/advance tasks — refresh those so the Enrollments and Tasks
  // surfaces stay live alongside the inbox (no extra polling needed for the
  // high-signal reply event). The enrollment list key embeds jobId, which
  // the notification payload doesn't carry, so match by URL substring.
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key0 = query.queryKey[0];
      return (
        typeof key0 === "string" &&
        key0.includes(`/sequences/${sequenceId}/enrollments`)
      );
    },
  });
  queryClient.invalidateQueries({ queryKey: getListTasksQueryKey() });
  queryClient.invalidateQueries({ queryKey: getGetTaskCountsQueryKey() });
}

/**
 * Refresh the outreach surfaces for an ephemeral ``sequence_event`` — the
 * backend fires these whenever an enrollment's status / engagement /
 * outcome changes, a task is created or transitions, or a terminal
 * finalizer archives tasks. No bell row is created; this is pure cache
 * invalidation so open Enrollments / Tasks / Inbox views update live.
 *
 * Exported: shared with `useSequenceEventsPoll` (the per-sequence
 * long-poll every viewer of a sequence's surfaces runs) so both realtime
 * paths invalidate identically.
 */
export function applySequenceEvent(
  queryClient: ReturnType<typeof useQueryClient>,
  ev: {
    sequence_id?: string | null;
    job_id?: string | null;
    scope?: string | null;
  },
): void {
  const scope = ev.scope ?? "all";
  const sequenceId = asString(ev.sequence_id);

  if (scope === "tasks" || scope === "all") {
    queryClient.invalidateQueries({ queryKey: getListTasksQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetTaskCountsQueryKey() });
  }

  if ((scope === "enrollments" || scope === "all") && sequenceId) {
    // One predicate covers every query scoped to this sequence: the
    // enrollment list (key embeds jobId, matched by substring), the
    // sequence detail, and the inbox summary/threads/chats — so the
    // status, outcome, and engagement columns all refresh together.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key0 = query.queryKey[0];
        return (
          typeof key0 === "string" &&
          key0.includes(`/sequences/${sequenceId}`)
        );
      },
    });
  }
}

export function useNotificationPoll() {
  const queryClient = useQueryClient();

  useEffect(() => {
    let channel: BroadcastChannel | null = null;

    try {
      channel = new BroadcastChannel("arbi-notifications");
    } catch {
      // BroadcastChannel not supported (SSR / old browser) — skip silently
      return;
    }

    let isLeader = false;
    let controller: AbortController | null = null;
    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
    let watchdogTimeout: ReturnType<typeof setTimeout> | null = null;
    let destroyed = false;
    // Random rank breaks a two-leader tie deterministically: if two tabs
    // claim leadership in the same window, the higher-rank one steps down
    // when it hears the lower-rank peer's heartbeat. Without this, both
    // tabs poll AND rebroadcast, so each notification is applied twice.
    const myRank = Math.random();

    // Dedup guard: a notification can reach this tab more than once (poll
    // replay overlap, a brief two-leader window, the sibling rebroadcast,
    // React Strict Mode double-mount in dev). Apply each notification id
    // exactly once so we never double-toast or double-invalidate.
    const appliedNotifIds = new Set<string>();
    const appliedOrder: string[] = [];
    function markApplied(id: string): boolean {
      if (appliedNotifIds.has(id)) return false;
      appliedNotifIds.add(id);
      appliedOrder.push(id);
      if (appliedOrder.length > 500) {
        const evicted = appliedOrder.shift();
        if (evicted) appliedNotifIds.delete(evicted);
      }
      return true;
    }

    function applyEvents(events: PollEvent[]) {
      if (!events.length) return;

      // Apply balance from the event — do not invalidate/refetch /credits.
      // Under sustained sync/reveal debits the backend publishes these every
      // ~0.75–3s; refetching the full credits payload each time floods the API.
      for (const ev of events) {
        if (!isCreditEvent(ev)) continue;
        if (typeof ev.balance === "number") {
          const balance = ev.balance;
          queryClient.setQueryData<{ balance: number }>(
            ["billing", "credits"],
            (old) => (old ? { ...old, balance } : { balance }),
          );
        } else {
          queryClient.invalidateQueries({ queryKey: ["billing", "credits"] });
        }
      }

      // A sequence schedule edit fans out one sequence_event per in-flight
      // enrollment (each driver workflow republishes as it re-persists its
      // due time), so a single poll batch can carry hundreds of identical
      // (sequence_id, scope) events. Dedupe within the batch — one
      // invalidation per distinct target — so the enrollments query isn't
      // churned once per enrollment.
      const seenSequenceEvents = new Set<string>();
      for (const ev of events) {
        if (!isSequenceEvent(ev)) continue;
        const key = `${ev.sequence_id ?? ""}|${ev.scope ?? "all"}`;
        if (seenSequenceEvents.has(key)) continue;
        seenSequenceEvents.add(key);
        applySequenceEvent(queryClient, ev);
      }

      // Real bell notifications — dedup by id before toasting / invalidating.
      const newNotifs = events
        .filter(isNotification)
        .filter((ev) => !ev.id || markApplied(ev.id));
      if (!newNotifs.length) return;

      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      // A finished export's bell entry: settle its job now, not at its next
      // (up to 15 s) poll.
      if (newNotifs.some((ev) => ev.category === "exports")) {
        refreshExportJobs(queryClient);
      }
      for (const ev of newNotifs) {
        if (PERSONAL_TOAST_CATEGORIES.has(ev.category)) {
          showNotificationToast(ev);
        }
        // A "candidate replied" notification carries the inbox location
        // in metadata.payload — refresh the relevant inbox queries so an
        // open inbox updates in real time instead of waiting on a poll.
        invalidateInboxForReply(queryClient, ev);
      }
    }

    async function runPollLoop(signal: AbortSignal) {
      let since = new Date().toISOString();
      while (!signal.aborted && !destroyed) {
        try {
          const result = (await pollNotifications(
            { since, timeout: 25 },
            { signal },
          )) as PollResult;
          since = result.server_time;
          if (result.events.length > 0) {
            // Broadcast to sibling tabs before applying locally
            channel?.postMessage({
              type: "events",
              events: result.events,
            } satisfies BroadcastMsg);
            applyEvents(result.events);
          }
        } catch {
          if (signal.aborted || destroyed) break;
          await sleep(2000);
        }
      }
    }

    function becomeLeader() {
      if (destroyed || isLeader) return;
      isLeader = true;
      // Heartbeat so other tabs know someone is leading
      heartbeatInterval = setInterval(() => {
        channel?.postMessage({
          type: "leader-alive",
          rank: myRank,
        } satisfies BroadcastMsg);
      }, 2000);
      controller = new AbortController();
      runPollLoop(controller.signal);
    }

    function stepDown() {
      if (!isLeader) return;
      isLeader = false;
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }
      controller?.abort();
      controller = null;
      // Re-arm the watchdog so this tab reclaims leadership if the
      // surviving leader's heartbeat ever stops (its tab closes).
      resetWatchdog();
    }

    function resetWatchdog() {
      if (watchdogTimeout) clearTimeout(watchdogTimeout);
      watchdogTimeout = setTimeout(() => {
        if (!isLeader && !destroyed) becomeLeader();
      }, 5000);
    }

    channel.onmessage = (msg: MessageEvent<BroadcastMsg>) => {
      const data = msg.data;
      if (data.type === "leader-alive") {
        resetWatchdog();
        // Two leaders elected in the same window: the higher-rank tab
        // yields so only one poll loop survives.
        if (isLeader && data.rank < myRank) stepDown();
      } else if (data.type === "events") {
        // Sibling tab received events from the leader — apply them
        applyEvents(data.events);
      }
    };

    // Defer leader claim with a little jitter so two tabs opened together
    // don't both claim in the same instant (the rank tiebreak still covers
    // the residual race).
    watchdogTimeout = setTimeout(
      () => {
        if (!isLeader && !destroyed) becomeLeader();
      },
      300 + Math.floor(Math.random() * 400),
    );

    return () => {
      destroyed = true;
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (watchdogTimeout) clearTimeout(watchdogTimeout);
      controller?.abort();
      channel?.close();
    };
  }, [queryClient]);
}
